/**
 * AskArloPanel.js - The "What should I watch?" chat overlay.
 *
 * Always mounted inside AskArloProvider and renders null while closed, so
 * the transcript survives close/reopen within a session. Closing cancels
 * any in-flight turn. Centered dialog on md+, full-width bottom sheet on
 * mobile. Styled to the PDPModal design family: #1a1a1a surface, blurred
 * black backdrop, 8px radius, Sports.TV teal (#FCC834) accent, fronted by
 * the Arlo "A" mark.
 */
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAskArloPanel } from '../../contexts/AskArloContext';
import useAskArlo, { MOOD_CHIPS } from '../../hooks/useAskArlo';
import AskArloCard from './AskArloCard';
import ArloAttributionCaption from '../ArloAttributionCaption';
import arloService from '../../services/ArloService';

// Tappable chip pills. Both cold-start mood chips and per-turn quick replies
// are the same affordance: a tap sends that phrase as an ordinary chat turn.
const ChipRow = ({ chips, onTap, disabled, testid }) => (
  <div className="flex flex-wrap gap-2" data-testid={testid}>
    {chips.map(label => (
      <button
        key={label}
        type="button"
        disabled={disabled}
        onClick={() => onTap(label)}
        className="px-3 py-1.5 text-sm rounded-full border border-[#FCC834]/40 text-[#7fd3c7] bg-[#FCC834]/10 hover:bg-[#FCC834]/20 disabled:opacity-40 disabled:cursor-not-allowed"
      >
        {label}
      </button>
    ))}
  </div>
);

const AskArloPanel = () => {
  const navigate = useNavigate();
  const { isOpen, closePanel, prefill, clearPrefill } = useAskArloPanel();
  const {
    messages,
    streaming,
    error,
    attribution,
    chips,
    turnCapReached,
    send,
    retry,
    reset,
    cancel,
  } = useAskArlo();
  const [input, setInput] = useState('');
  const attributionReported = useRef(false);
  const transcriptRef = useRef(null);

  // Search escape hatch: empty transcript → the failed query auto-sends as
  // turn 1; otherwise it lands in the input so an existing conversation
  // isn't hijacked.
  useEffect(() => {
    if (!isOpen || !prefill) {return;}
    if (messages.length === 0) {
      send(prefill);
    } else {
      setInput(prefill);
    }
    clearPrefill();
  }, [isOpen, prefill, messages.length, send, clearPrefill]);

  // Panel close cancels any in-flight turn (spec).
  useEffect(() => {
    if (!isOpen) {cancel();}
  }, [isOpen, cancel]);

  // Escape closes; body scroll locked while open (same contract as PDPModal).
  useEffect(() => {
    if (!isOpen) {return undefined;}
    const onKeyDown = event => {
      if (event.key === 'Escape') {closePanel();}
    };
    document.addEventListener('keydown', onKeyDown);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = prevOverflow;
    };
  }, [isOpen, closePanel]);

  const showAttribution = Boolean(attribution?.required);

  // Compliance: report the attribution exactly once per surface. The panel
  // stays mounted across open/close, so the ref spans the whole session.
  useEffect(() => {
    if (isOpen && showAttribution && !attributionReported.current) {
      attributionReported.current = true;
      arloService.attributionRendered();
    }
  }, [isOpen, showAttribution]);

  useEffect(() => {
    if (transcriptRef.current && typeof transcriptRef.current.scrollTo === 'function') {
      transcriptRef.current.scrollTo({ top: transcriptRef.current.scrollHeight });
    }
  }, [messages]);

  if (!isOpen) {return null;}

  const handleSend = () => {
    const trimmed = input.trim();
    if (!trimmed || streaming || turnCapReached) {return;}
    send(trimmed);
    setInput('');
  };

  // A chip tap is identical to the user typing that phrase and hitting send.
  const handleChipTap = phrase => {
    if (streaming || turnCapReached) {return;}
    setInput('');
    send(phrase);
  };

  const handlePickClick = pick => {
    arloService.track('pick_tapped', {
      rec_id: pick.recId,
      content_id: pick.channel.slug,
    });
    closePanel();
    // PDP, not the watch page — users need details/pricing before playback.
    // The site's canonical PDP is the homepage's ?channel= modal (same URL
    // shape as social shares and the movies/series pages); it handles movies
    // and series alike. Passing the resolved channel in state lets the modal
    // open instantly with subscription data; arloRecId rides along so a
    // future play-conversion report can join back to this rec.
    navigate(`/?channel=${pick.channel.slug}`, {
      state: { channel: pick.channel, arloRecId: pick.recId, from: 'ask-arlo' },
    });
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/80 backdrop-blur-sm md:items-center"
      onClick={closePanel}
      data-testid="ask-arlo-overlay"
    >
      <div
        role="dialog"
        aria-label="What should I watch?"
        onClick={event => event.stopPropagation()}
        className="flex flex-col w-full max-h-[85vh] md:max-w-2xl md:h-[70vh] bg-[#1a1a1a] border border-white/10 rounded-t-lg md:rounded-lg overflow-hidden shadow-[0_25px_50px_rgba(0,0,0,0.7)]"
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
          <div className="flex items-center gap-2.5">
            <img
              src="/assets/images/arlo-mark.svg"
              alt=""
              aria-hidden="true"
              className="w-6 h-6 shrink-0"
            />
            <h2 className="text-lg font-semibold text-white">What should I watch?</h2>
          </div>
          <div className="flex items-center gap-4">
            {messages.length > 0 && !turnCapReached && (
              <button
                type="button"
                onClick={reset}
                className="text-sm text-white/50 hover:text-white"
              >
                Start over
              </button>
            )}
            <button
              type="button"
              onClick={closePanel}
              aria-label="Close"
              className="flex items-center justify-center rounded-full w-9 h-9 shrink-0 text-white/70 bg-black/60 hover:text-white hover:bg-black/80"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="w-5 h-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <div ref={transcriptRef} className="flex-1 min-h-[16rem] px-5 py-4 space-y-4 overflow-y-auto">
          {messages.length === 0 && (
            <div className="space-y-4">
              <p className="text-white/50">
                Tell me what kind of night it is — I&apos;ll find you something.
              </p>
              <ChipRow
                chips={MOOD_CHIPS}
                onTap={handleChipTap}
                disabled={streaming}
                testid="ask-arlo-mood-chips"
              />
            </div>
          )}
          {messages.map((message, index) =>
            message.role === 'user' ? (
              <div key={index} className="flex justify-end">
                <div className="max-w-[80%] px-4 py-2 text-white bg-white/10 rounded-2xl">
                  {message.content}
                </div>
              </div>
            ) : (
              <div key={index} className="space-y-3">
                {message.content ? (
                  <p className="whitespace-pre-wrap text-white/90">{message.content}</p>
                ) : streaming && index === messages.length - 1 ? (
                  <p className="text-white/50">Thinking…</p>
                ) : null}
                {message.pendingPicks > 0 ? (
                  <div
                    className="grid grid-cols-2 gap-3 sm:grid-cols-3"
                    data-testid="ask-arlo-pick-skeletons"
                  >
                    {Array.from({ length: Math.min(message.pendingPicks, 6) }, (_, i) => (
                      <div key={i} className="animate-pulse">
                        <div className="w-full aspect-[2/3] rounded-lg bg-white/5 border border-white/10" />
                        <div className="w-3/4 h-4 mt-2 rounded bg-white/5" />
                      </div>
                    ))}
                  </div>
                ) : message.picks.length > 0 ? (
                  <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                    {message.picks.map(pick => (
                      <AskArloCard key={pick.channel.slug} pick={pick} onClick={handlePickClick} />
                    ))}
                  </div>
                ) : null}
              </div>
            ),
          )}
          {error && (
            <div className="flex items-center gap-3">
              <p className="text-sm text-white/60">{error}</p>
              <button
                type="button"
                onClick={retry}
                className="text-sm font-medium text-[#FCC834] hover:text-[#7fd3c7]"
              >
                Try again
              </button>
            </div>
          )}
          {messages.length > 0 && chips.length > 0 && !streaming && !turnCapReached && (
            <ChipRow
              chips={chips}
              onTap={handleChipTap}
              disabled={streaming}
              testid="ask-arlo-chips"
            />
          )}
        </div>

        <div className="px-5 py-4 border-t border-white/10">
          {turnCapReached ? (
            <button
              type="button"
              onClick={reset}
              className="w-full py-2 text-sm font-medium text-white rounded-full bg-white/10 hover:bg-white/15"
            >
              Start over
            </button>
          ) : (
            <form
              onSubmit={event => {
                event.preventDefault();
                handleSend();
              }}
              className="flex items-center gap-3"
            >
              <input
                autoFocus
                value={input}
                onChange={event => setInput(event.target.value)}
                placeholder="What kind of night is it?"
                data-testid="ask-arlo-input"
                className="flex-1 px-4 py-2 text-sm text-white border rounded-full outline-none bg-white/5 border-white/15 placeholder-white/40 focus:border-[#FCC834]/60"
              />
              <button
                type="submit"
                disabled={streaming || !input.trim()}
                className="px-4 py-2 text-sm font-medium text-[#0d1a18] rounded-full bg-[#FCC834] hover:bg-[#7fd3c7] disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Send
              </button>
            </form>
          )}
          {showAttribution && (
            <div className="mt-3 text-center">
              <ArloAttributionCaption attribution={attribution} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AskArloPanel;
